// @owners { team: patients-team }
import { COLORS, SIZES, SPACING } from '@alto/design-library-tokens';
import {
  Card,
  Column,
  ContentLoader,
  Description,
  ExpandableImage,
  H1,
  InlineAlert,
  LgPadding,
  LgSpacing,
  MdSpacing,
  Row,
  SecondaryPage,
  TagText,
  XsSpacing,
  XxsSpacing,
  getBrand,
  useScreenSize,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { isNative } from '~shared/helpers/isNative';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { CartLauncherButton, useAddToCartFlow } from '../../cart';
import { PageErrorFallback } from '../../error-boundary';
import { Pill } from '../../med-list';
import { YoutubePlayer } from '../../video/components/YoutubePlayer';
import { useMedDetailsMedication } from '../queries/useMedDetails';
import { MedicationDescriptions } from './MedicationDescriptions';
import { OtherPrescriptions } from './OtherPrescriptions';
import { PrescriptionCard } from './prescription-card';

const SmExpandableImageWrapper = styled(View)`
  width: ${SIZES.ILLUSTRATION.SM.px};
`;

const TitleRow = styled(Row)`
  min-width: 0;
`;

const ImagePillWrapper = styled(View)`
  flex-shrink: 0;
`;

const MdImageSizedOffset = styled(View)`
  width: ${SIZES.ILLUSTRATION.MD.px};
`;

const getHeaderBackgroundColor = () => {
  const brand = getBrand();
  if (brand === 'fuzehealth') {
    return COLORS.BACKGROUND_COLORS.TERTIARY_LIGHTEST;
  }
  return COLORS.BACKGROUND_COLORS.GREY_LIGHTEST;
};

export const MedDetails = ({
  origin,
  primaryPrescriptionId,
}: {
  readonly origin: string;
  readonly primaryPrescriptionId: number;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { trackPageView } = useAnalytics();
  const { goBack, setOptions } = useNavigation<'RouteMedDetails'>();
  const { isFetching, medication } = useMedDetailsMedication({ primaryPrescriptionId });
  const hasMedication = medication !== undefined && medication !== null;
  const { isMDScreenOrBigger } = useScreenSize();
  const { cartSize } = useCartData();
  const { handleAddToCart } = useAddToCartFlow({ origin: ORIGIN_NAMES.MED_DETAILS });
  const containerRef = useRef(null);
  const [playerSize, setPlayerSize] = useState({ width: 0, height: 0 });

  // setOptions is here to override the header options in meds.tsx - we don't show a header on this page
  useEffect(() => {
    setOptions({ headerShown: false });
  }, [setOptions]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.MED_DETAILS__VIEWED,
      params: {
        origin,
        medicationName: medication?.title,
        medicationStatus: medication?.primary_prescription?.status?.title,
        hasOtherPrescriptions: medication?.has_other_prescriptions,
        videoID: medication?.primary_prescription?.instructional_video?.id,
      },
    });
  }, [
    trackPageView,
    origin,
    medication?.title,
    medication?.primary_prescription?.status?.title,
    medication?.has_other_prescriptions,
    medication?.primary_prescription?.instructional_video?.id,
  ]);

  if (isFetching && !hasMedication) {
    return (
      <SecondaryPage
        HeaderContent={
          <LgPadding
            topPadding={SPACING.STATIC.XS}
            bottomPadding={SPACING.STATIC.MD}
            leftPadding={isMDScreenOrBigger ? SPACING.STATIC.NONE : SPACING.STATIC.LG}
            rightPadding={isMDScreenOrBigger ? SPACING.STATIC.NONE : SPACING.STATIC.LG}
          >
            <Row
              flexShrink={1}
              flexGrow={1}
            >
              <H1>Medication details</H1>
            </Row>
          </LgPadding>
        }
        headerBackgroundColor={getHeaderBackgroundColor()}
        dismissIcon="chevronleft"
        onDismiss={goBack}
      >
        <LgSpacing />
        <Card>
          <LgPadding>
            <Column>
              <ContentLoader accessibilityLabel="loading medication details" />
            </Column>
          </LgPadding>
        </Card>
      </SecondaryPage>
    );
  }

  if (!hasMedication) {
    return (
      <SecondaryPage
        headerBackgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
        dismissIcon="chevronleft"
        onDismiss={goBack}
      >
        <PageErrorFallback />
      </SecondaryPage>
    );
  }

  const { title, image_url, patient_name, stock_status, stock_details, primary_prescription, has_other_prescriptions } =
    medication;
  const { instructional_video: video } = primary_prescription || {};

  return (
    <SecondaryPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      headerBackgroundColor={getHeaderBackgroundColor()}
      buttons={isNative && cartSize > 0 ? [<CartLauncherButton key="med-details-cart-launcher-button" />] : []}
      HeaderContent={
        <LgPadding
          topPadding={SPACING.STATIC.XS}
          bottomPadding={SPACING.STATIC.MD}
          leftPadding={isMDScreenOrBigger ? SPACING.STATIC.NONE : SPACING.STATIC.LG}
          rightPadding={isMDScreenOrBigger ? SPACING.STATIC.NONE : SPACING.STATIC.LG}
        >
          <Column>
            {patient_name ? <Description color={COLORS.TEXT_COLORS.GREY}>{patient_name}</Description> : null}
            <XsSpacing />
            <Row
              wrap={false}
              top
            >
              <TitleRow
                flexShrink={1}
                flexGrow={1}
              >
                <H1>{title}</H1>
              </TitleRow>
              <MdSpacing />
              <ImagePillWrapper>
                {image_url ? (
                  <SmExpandableImageWrapper>
                    <ExpandableImage
                      accessibilityLabel={IMG_ALT_TEXTS.medication}
                      imageUrl={image_url}
                      height={SIZES.ILLUSTRATION.SM.value}
                    />
                  </SmExpandableImageWrapper>
                ) : (
                  <Pill />
                )}
              </ImagePillWrapper>
            </Row>
            {stock_status ? (
              <>
                <XsSpacing />
                <InlineAlert
                  type="error"
                  borderless
                >
                  <Row>
                    <TagText color={COLORS.TEXT_COLORS.PRIMARY}>{stock_status}</TagText>
                  </Row>
                  {stock_details ? (
                    <>
                      <XxsSpacing />
                      <Description color={COLORS.TEXT_COLORS.BLACK}>{stock_details}</Description>
                    </>
                  ) : null}
                </InlineAlert>
              </>
            ) : null}
            <XsSpacing />
            <Row wrap={false}>
              <MedicationDescriptions
                medication={medication}
                providerName={primary_prescription.prescriber}
              />
              <MdSpacing />
              <MdImageSizedOffset />
            </Row>
            {video ? (
              <View
                ref={containerRef}
                onLayout={(event) => {
                  const { width } = event.nativeEvent.layout;
                  const height = (width / 16) * 9; // maintain 16:9 aspect ratio
                  setPlayerSize({ width, height });
                }}
              >
                <XsSpacing />
                <YoutubePlayer
                  videoID={video.youtube_video_id}
                  width={playerSize.width}
                  height={playerSize.height}
                  origin={origin}
                />
              </View>
            ) : null}
          </Column>
        </LgPadding>
      }
    >
      <PrescriptionCard
        isPrimary
        prescription={primary_prescription}
        handleAddToCart={handleAddToCart}
        medicationName={title}
        primaryPrescriptionId={primaryPrescriptionId}
      />
      {has_other_prescriptions ? (
        <>
          <MdSpacing />
          <OtherPrescriptions
            primaryPrescriptionId={primaryPrescriptionId}
            handleAddToCart={handleAddToCart}
          />
        </>
      ) : null}
    </SecondaryPage>
  );
};
